import { useCallback, useEffect, useRef, useState } from 'react'
import { api, go, type Library, type MyLesson } from '../../lib/classroom'
import { L, w } from '../../lib/words'
import type { Lesson, Step } from '../../../shared/classroom'
import Icon from '../Icon'
import Pichi from './Pichi'
import { Rich } from './Step'

/**
 * /lecciones — a teacher's own library.
 *
 * Three ways in: draft with AI from a topic, upload a document the AI turns
 * into a lesson, or accept one another teacher shared. Everything lands as a
 * DRAFT; students never see it until the teacher reviews it, marks it ready
 * and adds it to a class. The rules live on the server (lessons.ts); this only
 * asks.
 */
export default function Lessons() {
  const [lib, setLib] = useState<Library | null>(null)
  const [error, setError] = useState<string | null>(null)
  const load = useCallback(async () => {
    try { setLib(await api.library()) } catch (e) { setError((e as Error).message) }
  }, [])
  useEffect(() => { void load() }, [load])

  if (error && !lib) return <p className="error card">{error}</p>
  if (!lib) return <div className="boot">…</div>

  return (
    <div className="lessons-page">
      <div className="guide-hero">
        <Pichi size={96} stage={2} mood="think" />
        <div>
          <h1>{w('myLessons')}</h1>
          <p className="muted small">{w('myLessonsSub')}</p>
        </div>
      </div>

      <p className="token-chip"><Icon name="coin" size={18} /> <b>{lib.tokens.balance}</b> {w('tokens')} <span className="muted small">· {w('tokensHint')}</span></p>

      {lib.incoming.length > 0 && <Incoming lib={lib} reload={load} />}

      {lib.ai ? <Create tokens={lib.tokens.balance} cost={lib.tokens.draftCost} onMade={load} /> : <p className="card muted">{w('aiOff')}</p>}

      <section className="card">
        <h2><Icon name="book" /> {w('myLessons')}</h2>
        {lib.lessons.length === 0 ? <p className="muted">{w('noLessonsYet')}</p> : (
          <ul className="my-lessons">
            {lib.lessons.map((l) => <LessonRow key={l.id} l={l} reload={load} />)}
          </ul>
        )}
      </section>
    </div>
  )
}

/* ── Creating ────────────────────────────────────────────────────────────── */

function Create({ tokens, cost, onMade }: { tokens: number; cost: number; onMade: () => Promise<void> }) {
  const [topic, setTopic] = useState('')
  const [grade, setGrade] = useState('')
  const [minutes, setMinutes] = useState('50')
  const [notes, setNotes] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [fileNotes, setFileNotes] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [made, setMade] = useState<MyLesson | null>(null)
  const fileInput = useRef<HTMLInputElement>(null)
  const broke = tokens < cost

  const run = async (fn: () => Promise<{ lesson: MyLesson }>) => {
    setBusy(true); setError(null); setMade(null)
    try {
      const { lesson } = await fn()
      setMade(lesson)
      setTopic(''); setNotes(''); setFile(null); setFileNotes('')
      if (fileInput.current) fileInput.current.value = ''
      await onMade()
    } catch (e) { setError((e as Error).message) } finally { setBusy(false) }
  }

  if (busy) {
    return (
      <section className="card drafting">
        <Pichi size={110} stage={2} mood="think" />
        <p><b>{w('drafting')}</b></p>
        <div className="drafting-bar"><span /></div>
      </section>
    )
  }

  return (
    <>
      {made && (
        <section className="card draft-made">
          <p><Icon name="check" /> <b>{w('draftReady')}</b> {L(made.title)}</p>
          <button className="primary" onClick={() => go(`/lecciones/${made.id}`)}><Icon name="eye" /> {w('review')}</button>
        </section>
      )}
      {error && <p className="error card" role="alert">{error}</p>}

      <section className="card create-card" style={{ ['--step' as string]: 'var(--pr-blue)' }}>
        <h2><Icon name="sparkle" /> {w('createWithAi')}</h2>
        <p className="muted small">{w('createWithAiSub')}</p>
        <label>{w('topic')}<input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder={w('topicPh')} maxLength={300} /></label>
        <div className="row">
          <label>{w('gradeLevel')}<input value={grade} onChange={(e) => setGrade(e.target.value)} placeholder="10-12" maxLength={60} /></label>
          <label>{w('classMinutes')}<input value={minutes} onChange={(e) => setMinutes(e.target.value.replace(/\D/g, ''))} inputMode="numeric" /></label>
        </div>
        <label>{w('notesForAi')}<textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={w('notesPh')} rows={2} maxLength={1000} /></label>
        <button className="primary" disabled={broke || topic.trim().length < 3}
          onClick={() => void run(() => api.draftWithAi({ topic: topic.trim(), grade: grade.trim(), minutes: Number(minutes) || 50, notes: notes.trim() }))}>
          <Icon name="sparkle" /> {w('draftIt')} <span className="small">· {cost} {w('tokens')}</span>
        </button>
      </section>

      <section className="card create-card" style={{ ['--step' as string]: 'var(--flamboyan)' }}>
        <h2><Icon name="download" /> {w('uploadLesson')}</h2>
        <p className="muted small">{w('uploadLessonSub')}</p>
        <input ref={fileInput} type="file" accept=".pdf,.docx,.txt,.md,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)} aria-label={w('chooseFile')} />
        <label>{w('notesForAi')}<textarea value={fileNotes} onChange={(e) => setFileNotes(e.target.value)} rows={2} maxLength={1000} /></label>
        <button className="primary" disabled={broke || !file} onClick={() => file && void run(() => api.draftFromFile(file, fileNotes.trim()))}>
          <Icon name="download" /> {w('draftIt')} <span className="small">· {cost} {w('tokens')}</span>
        </button>
      </section>
    </>
  )
}

/* ── The list ────────────────────────────────────────────────────────────── */

function LessonRow({ l, reload }: { l: MyLesson; reload: () => Promise<void> }) {
  const [sharing, setSharing] = useState(false)
  const [handle, setHandle] = useState('')
  const [msg, setMsg] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const act = async (fn: () => Promise<unknown>) => {
    setError(null)
    try { await fn(); await reload() } catch (e) { setError((e as Error).message) }
  }
  const source = l.source === 'ai' ? w('sourceAi') : l.source === 'upload' ? w('sourceUpload') : `${w('sourceShared')} ${l.sourceNote ?? ''}`
  return (
    <li className="my-lesson">
      <div className="my-lesson-head">
        <span className={`status-chip ${l.status}`}>{l.status === 'ready' ? w('ready') : w('draft')}</span>
        <span className="muted small">{source} · {l.minutes} min · {l.questions} {w('questions')}</span>
      </div>
      <strong className="my-lesson-title">{L(l.title)}</strong>
      <div className="my-lesson-actions">
        <button onClick={() => go(`/lecciones/${l.id}`)}><Icon name="eye" size={16} /> {w('review')}</button>
        {l.status === 'draft'
          ? <button onClick={() => void act(() => api.updateLesson(l.id, { status: 'ready' }))}><Icon name="check" size={16} /> {w('markReady')}</button>
          : <button onClick={() => setSharing(!sharing)}><Icon name="people" size={16} /> {w('share')}</button>}
        <button className="danger" aria-label={w('deleteForever')} onClick={() => void act(() => api.deleteLesson(l.id))}><Icon name="trash" size={16} /></button>
      </div>
      {sharing && (
        <div className="row">
          <input value={handle} onChange={(e) => setHandle(e.target.value)} placeholder={w('shareWith')} autoCapitalize="none" autoCorrect="off" />
          <button className="primary narrow" disabled={!handle.trim()} onClick={() => void act(async () => {
            const r = await api.shareLesson(l.id, handle.trim())
            setMsg(`${w('shared')} @${r.to}`); setHandle(''); setSharing(false)
          })}><Icon name="people" /> {w('share')}</button>
        </div>
      )}
      {msg && <p className="small"><Icon name="check" size={14} /> {msg}</p>}
      {error && <p className="error small">{error}</p>}
    </li>
  )
}

function Incoming({ lib, reload }: { lib: Library; reload: () => Promise<void> }) {
  const [error, setError] = useState<string | null>(null)
  const respond = async (id: string, accept: boolean) => {
    setError(null)
    try { await api.respondShare(id, accept); await reload() } catch (e) { setError((e as Error).message) }
  }
  return (
    <section className="card incoming">
      <h2><Icon name="people" /> {w('incoming')}</h2>
      <ul className="list">
        {lib.incoming.map((s) => (
          <li key={s.id}>
            <span><strong>{L(s.title)}</strong><br /><span className="muted small">{s.from} @{s.fromHandle}</span></span>
            <span className="row">
              <button className="primary narrow" onClick={() => void respond(s.id, true)}><Icon name="check" /> {w('accept')}</button>
              <button onClick={() => void respond(s.id, false)}>{w('decline')}</button>
            </span>
          </li>
        ))}
      </ul>
      {error && <p className="error">{error}</p>}
    </section>
  )
}

/* ── Reviewing one lesson: /lecciones/:id ────────────────────────────────── */

export function LessonReview({ id }: { id: string }) {
  const [data, setData] = useState<{ lesson: Lesson; meta: MyLesson } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const load = useCallback(async () => {
    try { setData(await api.lesson(id)) } catch (e) { setError((e as Error).message) }
  }, [id])
  useEffect(() => { void load() }, [load])
  if (error) return <p className="error card">{error}</p>
  if (!data) return <div className="boot">…</div>
  const { lesson, meta } = data
  const toggle = async () => {
    setError(null)
    try { await api.updateLesson(id, { status: meta.status === 'ready' ? 'draft' : 'ready' }); await load() } catch (e) { setError((e as Error).message) }
  }
  return (
    <div className="lessons-page">
      <button className="link" onClick={() => go('/lecciones')}><Icon name="back" size={16} /> {w('myLessons')}</button>
      <section className="card">
        <span className={`status-chip ${meta.status}`}>{meta.status === 'ready' ? w('ready') : w('draft')}</span>
        <h1>{L(lesson.title)}</h1>
        <p className="muted">{L(lesson.summary)}</p>
        <p className="small">{lesson.minutes} min · {meta.questions} {w('questions')}</p>
        <ul className="small">{lesson.objectives.map((o, i) => <li key={i}>{L(o)}</li>)}</ul>
        <p className="guide-tip"><Icon name="info" size={16} /> {w('lessonReviewNote')}</p>
        <button className="primary" onClick={() => void toggle()}>
          <Icon name="check" /> {meta.status === 'ready' ? w('backToDraft') : w('markReady')}
        </button>
      </section>
      <ol className="review-steps">
        {lesson.steps.map((s) => <ReviewStep key={s.id} step={s} />)}
      </ol>
      <section className="card"><h2><Icon name="book" /> {w('homework')}</h2><Rich text={L(lesson.homework)} /></section>
    </div>
  )
}

function ReviewStep({ step }: { step: Step }) {
  const kind = step.type === 'question' ? step.kind : step.type
  return (
    <li className={`review-step rs-${step.type}`}>
      <span className="muted small">{step.id} · {kind}</span>
      {'title' in step && <h3>{L(step.title)}</h3>}
      {'body' in step && <Rich text={L(step.body)} />}
      {'prompt' in step && <p><b><Rich text={L(step.prompt)} /></b></p>}
      {step.type === 'question' && step.kind === 'yesno' && <p className="right-answer"><Icon name="check" size={16} /> {w('correct')}: {step.answer ? w('yes') : w('no')}</p>}
      {(step.type === 'question' && step.kind === 'choice') && (
        <ol type="A">{step.choices.map((c, i) => <li key={i} className={i === step.answer ? 'right-answer' : ''}>{L(c)} {i === step.answer && <Icon name="check" size={14} />}</li>)}</ol>
      )}
      {step.type === 'poll' && <ul>{step.choices.map((c, i) => <li key={i}>{L(c)}</li>)}</ul>}
      {step.type === 'question' && step.kind === 'guess' && <p className="right-answer"><Icon name="check" size={16} /> {w('correct')}: {step.unit === '$' ? '$' : ''}{step.answer.toLocaleString()}{step.unit && step.unit !== '$' ? ` ${step.unit}` : ''}</p>}
      {step.type === 'question' && <p className="small muted"><Rich text={L(step.explain)} /></p>}
      {step.teacherNote && <p className="small teacher-note"><Icon name="info" size={14} /> <b>{w('teacherNoteLabel')}:</b> {L(step.teacherNote)}</p>}
    </li>
  )
}
